import {
  ActionIcon,
  Button,
  Drawer,
  Group,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { IconCopy, IconPlus } from "@tabler/icons-react";
import { api } from "@/lib/api";
import { qk } from "@/lib/apiHelpers";
import { driverFullCopyText } from "@/lib/driverCopy";
import type { Carrier, DriversListResponse, Vehicle, VehiclesListResponse } from "@/types/api";

const KIND_LABELS: Record<string, string> = {
  single: "Одиночная",
  autopoezd: "Автопоезд",
};

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => notifications.show({ message: "Скопировано в буфер", color: "teal" }),
    () => notifications.show({ message: "Не удалось скопировать", color: "red" }),
  );
}

function vehicleCopyText(v: Vehicle): string {
  const parts = [`${v.brand}${v.model ? " " + v.model : ""}`, `госномер ${v.license_plate}`];
  if (v.kind === "autopoezd" && v.trailer_brand) {
    parts.push(`прицеп ${v.trailer_brand} ${v.trailer_license_plate ?? ""}`);
  }
  if (v.capacity_kg) parts.push(`грузоподъёмность ${v.capacity_kg} кг`);
  return parts.join(", ");
}

export function CarrierFleet({
  carrier,
  opened,
  onClose,
}: {
  carrier: Carrier;
  opened: boolean;
  onClose: () => void;
}) {
  const { data: vehiclesData, isLoading: vehiclesLoading } = useQuery({
    queryKey: qk.vehicles({ carrier_id: carrier.id, page: 1, page_size: 200 }),
    queryFn: async () =>
      (
        await api.get<VehiclesListResponse>(
          `/api/v1/vehicles?carrier_id=${carrier.id}&page=1&page_size=200`,
        )
      ).data,
    enabled: opened,
  });

  const { data: driversData, isLoading: driversLoading } = useQuery({
    queryKey: qk.drivers({ carrier_id: carrier.id, page: 1, page_size: 200 }),
    queryFn: async () =>
      (
        await api.get<DriversListResponse>(
          `/api/v1/drivers?carrier_id=${carrier.id}&page=1&page_size=200`,
        )
      ).data,
    enabled: opened,
  });

  const vehicles = vehiclesData?.items ?? [];
  const drivers = driversData?.items ?? [];
  const navigate = useNavigate();

  // Кнопка «+ Создать машину»/«+ Создать водителя» — навигация в справочник
  // с query-параметрами, чтобы страница сразу открыла форму создания с
  // привязкой к этому перевозчику.
  const goCreateVehicle = () => {
    onClose();
    navigate(`/vehicles?new=1&carrier_id=${carrier.id}`);
  };
  const goCreateDriver = () => {
    onClose();
    navigate(`/drivers?new=1&carrier_id=${carrier.id}`);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <div>
          <Text fw={600}>Машины и водители</Text>
          <Text size="sm" c="dimmed">{carrier.name}</Text>
        </div>
      }
      position="right"
      size="xl"
    >
      <Stack gap="lg">
        {/* ---- Машины ---- */}
        <div>
          <Group justify="space-between" mb="sm">
            <Title order={4}>Машины</Title>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              variant="light"
              onClick={goCreateVehicle}
            >
              Создать машину
            </Button>
          </Group>
          {vehiclesLoading && <Text c="dimmed" size="sm">Загрузка...</Text>}
          {!vehiclesLoading && vehicles.length === 0 && (
            <Text c="dimmed" size="sm">Нет машин у этого перевозчика</Text>
          )}
          {vehicles.length > 0 && (
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Тип</Table.Th>
                  <Table.Th>Марка / Модель</Table.Th>
                  <Table.Th>Госномер</Table.Th>
                  <Table.Th>Грузоподъёмность</Table.Th>
                  <Table.Th style={{ width: 40 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {vehicles.map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>{KIND_LABELS[v.kind] ?? v.kind}</Table.Td>
                    <Table.Td>
                      {v.brand}{v.model ? ` ${v.model}` : ""}
                      {v.kind === "autopoezd" && v.trailer_brand
                        ? ` + ${v.trailer_brand}`
                        : ""}
                    </Table.Td>
                    <Table.Td>
                      {v.license_plate}
                      {v.kind === "autopoezd" && v.trailer_license_plate
                        ? ` / ${v.trailer_license_plate}`
                        : ""}
                    </Table.Td>
                    <Table.Td>{v.capacity_kg ? `${v.capacity_kg} кг` : "—"}</Table.Td>
                    <Table.Td>
                      <Tooltip label="Копировать" withArrow>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => copyToClipboard(vehicleCopyText(v))}
                        >
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </div>

        {/* ---- Водители ---- */}
        <div>
          <Group justify="space-between" mb="sm">
            <Title order={4}>Водители</Title>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              variant="light"
              onClick={goCreateDriver}
            >
              Создать водителя
            </Button>
          </Group>
          {driversLoading && <Text c="dimmed" size="sm">Загрузка...</Text>}
          {!driversLoading && drivers.length === 0 && (
            <Text c="dimmed" size="sm">Нет водителей у этого перевозчика</Text>
          )}
          {drivers.length > 0 && (
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ФИО</Table.Th>
                  <Table.Th>Телефон</Table.Th>
                  <Table.Th>ВУ</Table.Th>
                  <Table.Th style={{ width: 40 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {drivers.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>{d.full_name}</Table.Td>
                    <Table.Td>{d.phone ?? d.mobile ?? "—"}</Table.Td>
                    <Table.Td>{d.license_number ?? "—"}</Table.Td>
                    <Table.Td>
                      <Tooltip label="Копировать все данные" withArrow>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => copyToClipboard(driverFullCopyText(d))}
                        >
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </div>
      </Stack>

      {/* Spacer for Drawer scroll */}
      <Group h={40} />
    </Drawer>
  );
}
